import { Button } from "@design";
import { Monitor } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CenteredScreen } from "@/components/CenteredScreen";
import { Text } from "@/components/Text";
import { type Surface, useViewport } from "./viewport";

/**
 * Route-level adaptive features ("less feature rich on mobile").
 *
 * Every route that isn't available everywhere declares where it is, in ONE
 * place (app/router.tsx), so the mobile feature set is reviewable at a glance:
 *
 *   { path: "build", element: <OnSurfaces only={DESKTOP_ONLY}><BuildPage /></OnSurfaces> }
 *
 * Elsewhere, render-time checks use `availableOn(surfaces)` (e.g. hiding nav
 * entries). Routes without a wrapper are available on every surface.
 */

export const DESKTOP_ONLY: readonly Surface[] = ["tablet", "desktop"];

export function useAvailableOn(only: readonly Surface[]): boolean {
  return only.includes(useViewport().surface);
}

export function OnSurfaces({ only, children }: Readonly<{ only: readonly Surface[]; children: ReactNode }>) {
  return useAvailableOn(only) ? children : <NotOnThisDevice />;
}

function NotOnThisDevice() {
  const navigate = useNavigate();
  const { t } = useTranslation(["errors", "common"]);
  return (
    <CenteredScreen>
      <span className="text-content-tertiary">
        <Monitor size={40} aria-hidden="true" />
      </span>
      <Text variant="h2" as="h1">
        {t("largerScreen.title")}
      </Text>
      <Text tone="secondary">{t("largerScreen.message")}</Text>
      <Button variant="secondary" onClick={() => navigate("/")}>
        {t("common:goHome")}
      </Button>
    </CenteredScreen>
  );
}
